import { motion } from "framer-motion";
import { Phone, MessageCircle, CheckCircle2, Zap, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_IMAGE, PHONE_TEL, WHATSAPP_URL } from "@/constants/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" },
  }),
};

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 pt-16 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-24">
        <div className="relative z-10 flex flex-col justify-center lg:col-span-7">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-sm font-semibold text-orange-600"
            >
              <Zap className="h-4 w-4" /> Sgombero anche in giornata
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            data-testid="hero-heading"
            className="mt-6 text-4xl font-semibold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-7xl"
          >
            Svuotiamo casa, cantina e ufficio.
            <span className="text-orange-500"> Tu non pensi a niente.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            data-testid="hero-subheading"
            className="mt-6 max-w-xl text-base text-slate-600 md:text-lg"
          >
            Preventivo gratuito e senza sorprese: il prezzo che vedi è quello che paghi.
            Arriviamo, carichiamo, smaltiamo e lasciamo tutto pulito. Anche in giornata.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Button
              asChild
              size="lg"
              data-testid="hero-quote-button"
              className="rounded-md bg-orange-500 px-8 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.03] hover:bg-orange-600"
            >
              <a href="#preventivo">Richiedi preventivo gratuito</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-whatsapp-button"
              className="rounded-md border-green-600 px-8 text-base font-semibold text-green-700 transition-transform duration-200 hover:scale-[1.03] hover:bg-green-50"
            >
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" /> Scrivici su WhatsApp
              </a>
            </Button>
          </motion.div>

          <motion.ul
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-trust-list"
          >
            {["Preventivo gratuito", "Prezzo chiaro, senza sorprese", "Smaltimento incluso"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-orange-500" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative lg:col-span-5"
        >
          <div className="relative h-full min-h-[320px] overflow-hidden rounded-lg border border-slate-200 lg:min-h-[520px]">
            <img
              src={HERO_IMAGE}
              alt="Stanza vuota e pulita dopo lo sgombero"
              data-testid="hero-image"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rounded-md border border-slate-700 bg-slate-900/80 p-5 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <BadgeCheck className="h-8 w-8 shrink-0 text-orange-400" />
                <div>
                  <p className="font-display text-lg font-semibold text-white">Anni di esperienza, centinaia di sgomberi</p>
                  <p className="text-sm text-slate-300">Privati, eredità, uffici e agenzie immobiliari</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
